import { memo } from 'react';
import { Project, type ProjectResourceSummary } from '@/api/client';
import type { ProjectType } from '@/api/client';
import type { ComboConfig } from '@/hooks/useTableColumns';
import { ALL_COLUMN_DEFS, STATUS_BUTTON_GROUP, BUTTON_COLUMNS_GROUP, PROJECT_TABLE_ORDER } from '@/hooks/useTableColumns';
import { useAuth } from '@/contexts/useAuth';
import { AuthorCell } from './AuthorCell';
import { ReadersCell } from './ReadersCell';
import { ProducerCell } from './ProducerCell';
import { ContractorsCell } from './ContractorsCell';
import { StateCell } from './StateCell';
import { ProjectTypeCell } from './ProjectTypeCell';
import { EditableCell } from './EditableCell';
import { DeadlineCell } from './DeadlineCell';
import { ReviewDeadlineCell } from './ReviewDeadlineCell';
import { ReviewCheckTypeCell } from './ReviewCheckTypeCell';
import { PagesCell } from './PagesCell';
import { AuthorSheetsCell } from './AuthorSheetsCell';
import { TracksCountCell } from './TracksCountCell';
import { CorrectionsNumberCell } from './CorrectionsNumberCell';
import { SizeCell } from './SizeCell';
import { TimeCell } from './TimeCell';
import { PaperBookPlanCell } from './PaperBookPlanCell';
import { ThemeCell } from './ThemeCell';
import { DateLogCell } from './DateLogCell';
import { ComboCell } from './ComboCell';
import { ActionsCell } from './ActionsCell';
import { getProjectTypeText } from '@/utils/projectTypeColors';
import { getProjectTypeBadgeColor } from '@/styles/projectColorTokens';
import { formatDecimal } from '@/utils/formatNumber';
import type { ProjectTypeValue } from '@/utils/projectTypeColors';
import { Badge } from '@/components/ui/Badge/Badge';
import { ListIcon, DescriptionIcon, AttachFileIcon } from '@/components/ui/Icons';
import { PLANNER_STATES, getPlannerStatusInfo, isPlannerCriticalOrDangerStatus } from '@/utils/plannerStatusInfo';
import type { PlannerNameBadgeMode } from '@/hooks/usePlannerNameBadgeMode';
import { countNewNotes } from '@/utils/projectNotes';
import { countNewFiles } from '@/utils/projectFiles';
import { IconActionButton } from './IconActionButton';
import { StatusButtonCell } from './StatusButtonCell';
import { FonotekaProjectCell } from '@/features/fonoteka/FonotekaProjectCell';
import { filterFonotekaColumnIds } from '@/features/fonoteka/fonotekaColumnVisibility';
import { isMontageSession } from '@/utils/projectRecordingSessions';
import { highlightPlainText } from '@/utils/highlightSearchTerms';

const EmptyValue = () => <span className="projects-table__empty-value">N/A</span>;

const COMBO_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(ALL_COLUMN_DEFS).map(([id, def]) => [id, def.title])
);

export interface ProjectCellProps {
  project: Project;
  columnId: string;
  onDelete: (id: string) => void;
  onStateChange: (projectId: string, newState: string) => void;
  onProjectTypeChange: (projectId: string, newType: ProjectType | null) => void;
  isDeleting: boolean;
  combos: Record<string, ComboConfig> | undefined;
  atomicOrder: string[] | undefined;
  onOpenSessionsModal?: (project: Project, anchorEl: HTMLElement) => void;
  onOpenNotesPopover?: (project: Project, anchorEl: HTMLElement) => void;
  onOpenFilesPopover?: (project: Project, anchorEl: HTMLElement) => void;
  onOpenReviewCheckEmail?: (project: Project) => void;
  onOpenProductionHandoffEmail?: (project: Project) => void;
  onOpenPreArchiveUploadEmail?: (project: Project) => void;
  onOpenMountingCompleteEmail?: (project: Project) => void;
  onOpenCorrectionsAppliedEmail?: (project: Project) => void;
  onOpenProjectReviewedEmail?: (project: Project) => void;
  /** Открыть попап «Информация о проекте» (якорь — элемент интерфейса) */
  onOpenProjectDetails?: (anchorEl: HTMLElement | null) => void;
  typeBadgesEnabled: Record<ProjectTypeValue, boolean>;
  plannerNameBadgeMode: PlannerNameBadgeMode;
  /** Компактное отображение столбца «Чтец» */
  compactReaders?: boolean;
  /** Компактное отображение столбца «Контрагенты» */
  compactStudio?: boolean;
  resourceSummary?: ProjectResourceSummary;
  resourceLoadingByColumn?: Partial<Record<'sessions' | 'notes' | 'files', boolean>>;
  /** Слова активного поиска — подсветка совпадений в текстовых ячейках */
  searchHighlightWords?: string[];
}

function SessionsNotesFilesCell({
  project,
  columnId,
  onOpenSessionsModal,
  onOpenNotesPopover,
  onOpenFilesPopover,
  resourceSummary,
  resourceLoadingByColumn,
}: Pick<
  ProjectCellProps,
  | 'project'
  | 'onOpenSessionsModal'
  | 'onOpenNotesPopover'
  | 'onOpenFilesPopover'
  | 'resourceSummary'
  | 'resourceLoadingByColumn'
> & {
  columnId: string;
}) {
  const wrap = (content: React.ReactNode) => (
    <div className="projects-table__sessions-cell">{content}</div>
  );

  if (columnId === 'sessions' && onOpenSessionsModal) {
    const sessions = (project.sessions ?? []).filter((s) => !isMontageSession(s));
    const plannedCount =
      resourceSummary !== undefined
        ? resourceSummary.plannedSessionCount
        : sessions.filter((s) => !s.is_recorded && !s.is_canceled).length;
    const hasRecorded =
      resourceSummary !== undefined
        ? resourceSummary.recordedSessionCount > 0
        : sessions.filter((s) => s.is_recorded && !s.is_canceled).length > 0;
    return wrap(
      <IconActionButton
        icon={<ListIcon size={18} />}
        btnClass="projects-table__sessions-btn"
        hasContent={hasRecorded}
        isLoading={resourceLoadingByColumn?.sessions}
        newCount={plannedCount}
        newCountAriaLabel="Запланированных сессий"
        ariaLabel="Открыть записи сессий"
        onClick={(el) => onOpenSessionsModal(project, el)}
      />
    );
  }
  if (columnId === 'notes' && onOpenNotesPopover) {
    const hasNotes =
      resourceSummary !== undefined ? resourceSummary.noteCount > 0 : (project.notes ?? []).length > 0;
    const notesNewCount =
      resourceSummary !== undefined ? resourceSummary.recentNoteCount : countNewNotes(project.notes);
    return wrap(
      <IconActionButton
        icon={<DescriptionIcon size={18} />}
        btnClass="projects-table__notes-btn"
        hasContent={hasNotes}
        isLoading={resourceLoadingByColumn?.notes}
        newCount={notesNewCount}
        newCountAriaLabel="Новых заметок"
        ariaLabel="Открыть заметки проекта"
        onClick={(el) => onOpenNotesPopover(project, el)}
      />
    );
  }
  if (columnId === 'files' && onOpenFilesPopover) {
    const hasFiles =
      resourceSummary !== undefined ? resourceSummary.fileCount > 0 : (project.files ?? []).length > 0;
    const filesNewCount =
      resourceSummary !== undefined ? resourceSummary.recentFileCount : countNewFiles(project.files);
    return wrap(
      <IconActionButton
        icon={<AttachFileIcon size={18} />}
        btnClass="projects-table__files-btn"
        hasContent={hasFiles}
        isLoading={resourceLoadingByColumn?.files}
        newCount={filesNewCount}
        newCountAriaLabel="Новых файлов"
        ariaLabel="Открыть файлы проекта"
        onClick={(el) => onOpenFilesPopover(project, el)}
      />
    );
  }
  return wrap(null);
}

const STATUS_BUTTON_LABELS: Record<string, string> = {
  project_phonogram: 'Ф',
  project_dp: 'Ц',
  project_approved: 'С',
  project_done: 'D',
  project_pdf: 'PDF',
  has_music: 'Фонотека',
};

const STATUS_BUTTON_TITLES: Record<string, string> = {
  project_phonogram: 'Фонограмма',
  project_dp: 'ЦП',
  project_approved: 'Согласовано',
  project_done: 'Проект сдан',
  project_pdf: 'PDF',
  has_music: 'Фонотека',
};

function StatusFlagsComboCellContent({
  project,
  childIds,
  isDeleting,
}: Pick<ProjectCellProps, 'project' | 'isDeleting'> & { childIds: string[] }) {
  return (
    <div className="projects-table__sessions-cell projects-table__status-flags-combo-cell">
      {childIds.map((id) =>
        STATUS_BUTTON_GROUP.has(id) ? (
          <StatusButtonCell
            key={id}
            project={project}
            fieldId={id as import('./StatusButtonCell').StatusButtonFieldId}
            label={STATUS_BUTTON_LABELS[id] ?? id}
            title={STATUS_BUTTON_TITLES[id]}
            disabled={isDeleting}
          />
        ) : null
      )}
    </div>
  );
}

function ButtonsComboCellContent({
  project,
  childIds,
  onDelete,
  isDeleting,
  onOpenSessionsModal,
  onOpenNotesPopover,
  onOpenFilesPopover,
  onOpenReviewCheckEmail,
  onOpenProductionHandoffEmail,
  onOpenPreArchiveUploadEmail,
  onOpenMountingCompleteEmail,
  onOpenCorrectionsAppliedEmail,
  onOpenProjectReviewedEmail,
  onOpenProjectDetails,
  resourceSummary,
  resourceLoadingByColumn,
}: Pick<
  ProjectCellProps,
  | 'project'
  | 'onDelete'
  | 'isDeleting'
  | 'onOpenSessionsModal'
  | 'onOpenNotesPopover'
  | 'onOpenFilesPopover'
  | 'onOpenReviewCheckEmail'
  | 'onOpenProductionHandoffEmail'
  | 'onOpenPreArchiveUploadEmail'
  | 'onOpenMountingCompleteEmail'
  | 'onOpenCorrectionsAppliedEmail'
  | 'onOpenProjectReviewedEmail'
  | 'onOpenProjectDetails'
  | 'resourceSummary'
  | 'resourceLoadingByColumn'
> & { childIds: string[] }) {
  const { capabilities } = useAuth();
  const visibleChildIds = filterFonotekaColumnIds(childIds, capabilities.fonoteka.read);
  const sessions = (project.sessions ?? []).filter((s) => !isMontageSession(s));
  const sessionsPlannedCount =
    resourceSummary !== undefined
      ? resourceSummary.plannedSessionCount
      : sessions.filter((s) => !s.is_recorded && !s.is_canceled).length;
  const sessionsHasRecorded =
    resourceSummary !== undefined
      ? resourceSummary.recordedSessionCount > 0
      : sessions.filter((s) => s.is_recorded && !s.is_canceled).length > 0;

  return (
    <div className="projects-table__sessions-cell projects-table__menu-combo-cell projects-table__status-flags-combo-cell">
      {visibleChildIds.map((id) =>
        id === 'actions' ? (
          <ActionsCell
            key="actions"
            project={project}
            projectId={project.id}
            projectName={project.name}
            onDelete={onDelete}
            isDeleting={isDeleting}
            onOpenReviewCheckEmail={onOpenReviewCheckEmail}
            onOpenProductionHandoffEmail={onOpenProductionHandoffEmail}
            onOpenPreArchiveUploadEmail={onOpenPreArchiveUploadEmail}
            onOpenMountingCompleteEmail={onOpenMountingCompleteEmail}
            onOpenCorrectionsAppliedEmail={onOpenCorrectionsAppliedEmail}
            onOpenProjectReviewedEmail={onOpenProjectReviewedEmail}
            onOpenProjectDetails={onOpenProjectDetails}
          />
        ) : id === 'sessions' && onOpenSessionsModal ? (
          <IconActionButton
            key="sessions"
            icon={<ListIcon size={18} />}
            btnClass="projects-table__sessions-btn"
            hasContent={sessionsHasRecorded}
            isLoading={resourceLoadingByColumn?.sessions}
            newCount={sessionsPlannedCount}
            newCountAriaLabel="Запланированных сессий"
            ariaLabel="Открыть записи сессий"
            onClick={(el) => onOpenSessionsModal(project, el)}
          />
        ) : id === 'files' && onOpenFilesPopover ? (
          <IconActionButton
            key="files"
            icon={<AttachFileIcon size={18} />}
            btnClass="projects-table__files-btn"
            isLoading={resourceLoadingByColumn?.files}
            hasContent={
              resourceSummary !== undefined
                ? resourceSummary.fileCount > 0
                : (project.files ?? []).length > 0
            }
            newCount={
              resourceSummary !== undefined
                ? resourceSummary.recentFileCount
                : countNewFiles(project.files)
            }
            newCountAriaLabel="Новых файлов"
            ariaLabel="Открыть файлы проекта"
            onClick={(el) => onOpenFilesPopover(project, el)}
          />
        ) : id === 'notes' && onOpenNotesPopover ? (
          <IconActionButton
            key="notes"
            icon={<DescriptionIcon size={18} />}
            btnClass="projects-table__notes-btn"
            isLoading={resourceLoadingByColumn?.notes}
            hasContent={
              resourceSummary !== undefined
                ? resourceSummary.noteCount > 0
                : (project.notes ?? []).length > 0
            }
            newCount={
              resourceSummary !== undefined
                ? resourceSummary.recentNoteCount
                : countNewNotes(project.notes)
            }
            newCountAriaLabel="Новых заметок"
            ariaLabel="Открыть заметки проекта"
            onClick={(el) => onOpenNotesPopover(project, el)}
          />
        ) : id === 'has_music' ? (
          <FonotekaProjectCell key={id} project={project} disabled={isDeleting} />
        ) : STATUS_BUTTON_GROUP.has(id) ? (
          <StatusButtonCell
            key={id}
            project={project}
            fieldId={id as import('./StatusButtonCell').StatusButtonFieldId}
            label={STATUS_BUTTON_LABELS[id] ?? id}
            title={STATUS_BUTTON_TITLES[id]}
            disabled={isDeleting}
          />
        ) : null
      )}
    </div>
  );
}

export const ProjectCell = memo(function ProjectCell({
  project,
  columnId,
  onDelete,
  onStateChange,
  onProjectTypeChange,
  isDeleting,
  combos,
  atomicOrder,
  onOpenSessionsModal,
  onOpenNotesPopover,
  onOpenFilesPopover,
  onOpenReviewCheckEmail,
  onOpenProductionHandoffEmail,
  onOpenPreArchiveUploadEmail,
  onOpenMountingCompleteEmail,
  onOpenCorrectionsAppliedEmail,
  onOpenProjectReviewedEmail,
  onOpenProjectDetails,
  typeBadgesEnabled,
  plannerNameBadgeMode,
  compactReaders,
  compactStudio,
  resourceSummary,
  resourceLoadingByColumn,
  searchHighlightWords,
}: ProjectCellProps) {
  const readers = project.readers ?? [];
  const order = atomicOrder ?? PROJECT_TABLE_ORDER;
  const hl = (text: string) =>
    searchHighlightWords?.length ? highlightPlainText(text, searchHighlightWords) : text;

  switch (columnId) {
    case 'author': {
      const authors = project.authors ?? [];
      return (
        <EditableCell project={project} fieldKey="author">
          <AuthorCell authors={authors} searchHighlightWords={searchHighlightWords} />
        </EditableCell>
      );
    }
    case 'author_sheets':
      return <AuthorSheetsCell project={project} />;
    case 'tracks_count':
      return <TracksCountCell project={project} />;
    case 'corrections_number':
      return <CorrectionsNumberCell project={project} />;
    case 'size':
      return <SizeCell project={project} />;
    case 'name': {
      const typeText = getProjectTypeText(project.project_type);
      const typeBadgeColors =
        typeText && typeBadgesEnabled[project.project_type as ProjectTypeValue] !== false
          ? getProjectTypeBadgeColor(project.project_type)
          : undefined;
      const plannerStatus = resourceSummary?.plannerStatus ?? null;
      const plannerEligible =
        !project.is_archived &&
        plannerStatus !== null &&
        PLANNER_STATES.has(project.state ?? '') &&
        (plannerNameBadgeMode === 'all' ||
          (plannerNameBadgeMode === 'danger_only' && isPlannerCriticalOrDangerStatus(plannerStatus)));
      const plannerIcon = plannerEligible ? getPlannerStatusInfo(plannerStatus!) : null;
      return (
        <EditableCell project={project} fieldKey="name">
          <div className="projects-table__name-cell">
            {plannerIcon && onOpenProjectDetails && (
              <span
                className="projects-table__planner-icon"
                style={{ color: plannerIcon.badgeColor }}
                title={plannerIcon.label}
                role="button"
                tabIndex={0}
                aria-label="Детализация планера"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenProjectDetails(e.currentTarget);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenProjectDetails(e.currentTarget);
                  }
                }}
              >
                <plannerIcon.icon size={18} />
              </span>
            )}
            {plannerIcon && !onOpenProjectDetails && (
              <span
                className="projects-table__planner-icon"
                style={{ color: plannerIcon.badgeColor }}
                title={plannerIcon.label}
              >
                <plannerIcon.icon size={18} />
              </span>
            )}
            {typeText && typeBadgeColors ? (
              <Badge variant="custom" bg={typeBadgeColors.bg} color={typeBadgeColors.color}>
                {searchHighlightWords?.length ? hl(typeText) : typeText}
              </Badge>
            ) : null}
            <span className="projects-table__name-text">{hl(project.name)}</span>
          </div>
        </EditableCell>
      );
    }
    case 'readers':
      return (
        <EditableCell project={project} fieldKey="readers">
          <ReadersCell readers={readers} compact={compactReaders} searchHighlightWords={searchHighlightWords} />
        </EditableCell>
      );
    case 'theme':
      return <ThemeCell project={project} searchHighlightWords={searchHighlightWords} />;
    case 'rights':
      return (
        <EditableCell project={project} fieldKey="rights">
          {project.rights ? (
            <div className="projects-table__rights-cell">{hl(project.rights)}</div>
          ) : (
            <EmptyValue />
          )}
        </EditableCell>
      );
    case 'state':
      return (
        <StateCell
          value={project.state ?? 'Добавлен'}
          projectId={project.id}
          onChange={onStateChange}
          disabled={isDeleting}
          searchHighlightWords={searchHighlightWords}
        />
      );
    case 'project_type':
      return (
        <ProjectTypeCell
          value={project.project_type ?? null}
          projectId={project.id}
          onChange={onProjectTypeChange}
          disabled={isDeleting}
        />
      );
    case 'pages':
      return <PagesCell project={project} searchHighlightWords={searchHighlightWords} />;
    case 'time':
      return <TimeCell project={project} searchHighlightWords={searchHighlightWords} />;
    case 'recording_coef':
    case 'montage_coef': {
      const raw = columnId === 'recording_coef' ? project.recording_coef : project.montage_coef;
      const n = raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;
      return (
        <div className="projects-table__base-cell projects-table__cell_kf-coef">
          {n != null ? hl(formatDecimal(n)) : <EmptyValue />}
        </div>
      );
    }
    case 'menu_combo': {
      const combo = combos?.['menu_combo'];
      const childIds = combo?.childIds ?? ['files', 'notes', 'project_pdf', 'has_music'];
      const sorted = [...childIds].sort((a, b) => order.indexOf(a) - order.indexOf(b));
      return (
        <ButtonsComboCellContent
          project={project}
          childIds={sorted}
          onDelete={onDelete}
          isDeleting={isDeleting}
          onOpenSessionsModal={onOpenSessionsModal}
          onOpenNotesPopover={onOpenNotesPopover}
          onOpenFilesPopover={onOpenFilesPopover}
          onOpenReviewCheckEmail={onOpenReviewCheckEmail}
          onOpenProductionHandoffEmail={onOpenProductionHandoffEmail}
          onOpenPreArchiveUploadEmail={onOpenPreArchiveUploadEmail}
          onOpenMountingCompleteEmail={onOpenMountingCompleteEmail}
          onOpenCorrectionsAppliedEmail={onOpenCorrectionsAppliedEmail}
          onOpenProjectReviewedEmail={onOpenProjectReviewedEmail}
          onOpenProjectDetails={onOpenProjectDetails}
          resourceSummary={resourceSummary}
          resourceLoadingByColumn={resourceLoadingByColumn}
        />
      );
    }
    case 'status_flags_combo': {
      const combo = combos?.['status_flags_combo'];
      const childIds = combo?.childIds ?? ['project_phonogram', 'project_dp', 'project_approved', 'project_done'];
      const sorted = [...childIds].sort((a, b) => order.indexOf(a) - order.indexOf(b));
      return (
        <StatusFlagsComboCellContent
          project={project}
          childIds={sorted}
          isDeleting={isDeleting}
        />
      );
    }
    case 'project_phonogram':
    case 'project_dp':
    case 'project_approved':
    case 'project_done':
    case 'project_pdf':
      return (
        <div className="projects-table__sessions-cell">
          <StatusButtonCell
            project={project}
            fieldId={columnId as import('./StatusButtonCell').StatusButtonFieldId}
            label={STATUS_BUTTON_LABELS[columnId] ?? columnId}
            title={STATUS_BUTTON_TITLES[columnId]}
            disabled={isDeleting}
          />
        </div>
      );
    case 'has_music':
      return (
        <div className="projects-table__sessions-cell">
          <FonotekaProjectCell project={project} disabled={isDeleting} />
        </div>
      );
    case 'sessions':
    case 'notes':
    case 'files':
      return (
        <SessionsNotesFilesCell
          project={project}
          columnId={columnId}
          onOpenSessionsModal={onOpenSessionsModal}
          onOpenNotesPopover={onOpenNotesPopover}
          onOpenFilesPopover={onOpenFilesPopover}
          resourceSummary={resourceSummary}
          resourceLoadingByColumn={resourceLoadingByColumn}
        />
      );
    case 'deadline':
      return <DeadlineCell project={project} searchHighlightWords={searchHighlightWords} />;
    case 'review_check_type':
      return <ReviewCheckTypeCell project={project} searchHighlightWords={searchHighlightWords} />;
    case 'review_deadline':
      return <ReviewDeadlineCell project={project} searchHighlightWords={searchHighlightWords} />;
    case 'paper_book_plan':
      return <PaperBookPlanCell project={project} searchHighlightWords={searchHighlightWords} />;
    case 'asta':
    case 'ase':
    case 'rin': {
      const val = project[columnId as 'asta' | 'ase' | 'rin']?.trim() || null;
      return (
        <EditableCell project={project} fieldKey={columnId as 'asta' | 'ase' | 'rin'}>
          {val ? <div className="projects-table__base-cell">{hl(val)}</div> : <EmptyValue />}
        </EditableCell>
      );
    }
    case 'producer': {
      const names = (project.producers ?? []).map((p) => p.name);
      return (
        <EditableCell project={project} fieldKey="producer">
          <ProducerCell names={names} searchHighlightWords={searchHighlightWords} />
        </EditableCell>
      );
    }
    case 'techeditor': {
      const names = (project.techeditors ?? []).map((t) => t.name);
      return (
        <EditableCell project={project} fieldKey="techeditor">
          <ProducerCell names={names} searchHighlightWords={searchHighlightWords} />
        </EditableCell>
      );
    }
    case 'corrector': {
      const names = (project.correctors ?? []).map((c) => c.name);
      return (
        <EditableCell project={project} fieldKey="corrector">
          <ProducerCell names={names} searchHighlightWords={searchHighlightWords} />
        </EditableCell>
      );
    }
    case 'studio': {
      const contractors = project.project_contracts ?? project.contractors ?? [];
      return (
        <EditableCell project={project} fieldKey="studio">
          <ContractorsCell contractors={contractors} compact={compactStudio} searchHighlightWords={searchHighlightWords} />
        </EditableCell>
      );
    }
    case 'department': {
      const val = project.edition_department_code?.trim() ?? null;
      return (
        <EditableCell project={project} fieldKey="department">
          {val ? <div className="projects-table__base-cell">{hl(val)}</div> : <EmptyValue />}
        </EditableCell>
      );
    }
    case 'edition': {
      const val = project.edition_name?.trim() ?? null;
      return (
        <EditableCell project={project} fieldKey="edition">
          {val ? <div className="projects-table__base-cell">{hl(val)}</div> : <EmptyValue />}
        </EditableCell>
      );
    }
    case 'editor': {
      const val = project.editor_name?.trim() ?? null;
      return (
        <EditableCell project={project} fieldKey="editor">
          {val ? <div className="projects-table__base-cell">{hl(val)}</div> : <EmptyValue />}
        </EditableCell>
      );
    }
    case 'dates':
      return <DateLogCell project={project} />;
    case 'actions':
      return (
        <ActionsCell
          project={project}
          projectId={project.id}
          projectName={project.name}
          onDelete={onDelete}
          isDeleting={isDeleting}
          onOpenReviewCheckEmail={onOpenReviewCheckEmail}
          onOpenProductionHandoffEmail={onOpenProductionHandoffEmail}
          onOpenPreArchiveUploadEmail={onOpenPreArchiveUploadEmail}
          onOpenMountingCompleteEmail={onOpenMountingCompleteEmail}
          onOpenCorrectionsAppliedEmail={onOpenCorrectionsAppliedEmail}
          onOpenProjectReviewedEmail={onOpenProjectReviewedEmail}
          onOpenProjectDetails={onOpenProjectDetails}
        />
      );
    default: {
      const combo = combos?.[columnId];
      if (combo?.childIds?.length) {
        const sortedChildIds = [...combo.childIds].sort((a, b) => {
          const ia = order.indexOf(a);
          const ib = order.indexOf(b);
          return (ia === -1 ? 99999 : ia) - (ib === -1 ? 99999 : ib);
        });
        const isButtonsCombo = sortedChildIds.every((id) => BUTTON_COLUMNS_GROUP.has(id));
        if (isButtonsCombo) {
          return (
            <ButtonsComboCellContent
              project={project}
              childIds={sortedChildIds}
              onDelete={onDelete}
              isDeleting={isDeleting}
              onOpenSessionsModal={onOpenSessionsModal}
              onOpenNotesPopover={onOpenNotesPopover}
              onOpenFilesPopover={onOpenFilesPopover}
              onOpenReviewCheckEmail={onOpenReviewCheckEmail}
              onOpenProductionHandoffEmail={onOpenProductionHandoffEmail}
              onOpenPreArchiveUploadEmail={onOpenPreArchiveUploadEmail}
              onOpenMountingCompleteEmail={onOpenMountingCompleteEmail}
              onOpenCorrectionsAppliedEmail={onOpenCorrectionsAppliedEmail}
            onOpenProjectReviewedEmail={onOpenProjectReviewedEmail}
              onOpenProjectDetails={onOpenProjectDetails}
              resourceSummary={resourceSummary}
              resourceLoadingByColumn={resourceLoadingByColumn}
            />
          );
        }
        return (
          <EditableCell project={project} fieldKey="combo" comboChildIds={sortedChildIds} label={combo.title}>
            <ComboCell
              project={project}
              columnIds={sortedChildIds}
              labels={COMBO_LABELS}
              searchHighlightWords={searchHighlightWords}
            />
          </EditableCell>
        );
      }
      return <EmptyValue />;
    }
  }
});
